import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useCms } from "../CmsContext";
import { PageHeader } from "../EditorChrome";
import { TextField, TextAreaField, SelectField } from "../Fields";
import { PRIORITY_LABELS, STATUS_LABELS, formatTime, timeAgo } from "../cmsUtils";

const STATUS_ORDER = ["open", "in-progress", "done", "declined"];

function TicketAttachments({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="lw-attachlist">
      {attachments.map((url) => (
        <a
          key={url}
          className="lw-attachlist__item"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open full size"
        >
          <img src={url} alt="Ticket attachment" loading="lazy" />
        </a>
      ))}
    </div>
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const { api, API_BASE } = useCms();
  const [title, setTitle] = useState("");
  const [page, setPage] = useState("");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post(`${API_BASE}/tickets/upload`, formData);
      if (res && res.success && res.imageUrl) {
        uploaded.push(res.imageUrl);
      } else {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded.length > 0) setAttachments((prev) => [...prev, ...uploaded]);
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Give the request a title");
      return;
    }
    setSubmitting(true);
    const res = await api.post(`${API_BASE}/tickets/create`, {
      title: title.trim(),
      page: page.trim(),
      priority,
      description: description.trim(),
      attachments,
    });
    setSubmitting(false);
    if (res && res.success) {
      toast.success("Request submitted — the site maintainer has been emailed");
      onCreated();
    } else {
      toast.error(res?.error || "Failed to create request");
    }
  };

  return (
    <div className="lw-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lw-modal__panel">
        <div className="lw-modal__head">
          <h2 className="lw-modal__title">New change request</h2>
          <button type="button" className="lw-iconbtn" title="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="lw-modal__body">
          <p className="lw-modal__lede">
            For anything the CMS can’t do — layout tweaks, new sections, new functionality.
          </p>
          <div className="lw-fields">
            <TextField label="What needs to change?" value={title} onChange={setTitle} placeholder="e.g. Add a video to the Convene page hero" />
            <TextField label="Page or area (optional)" value={page} onChange={setPage} placeholder="e.g. /services/convene" />
            <SelectField
              label="Priority"
              value={priority}
              onChange={setPriority}
              options={STATUS_ORDER.length ? Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label })) : []}
            />
            <TextAreaField
              label="Details"
              value={description}
              onChange={setDescription}
              rows={5}
              hint="Describe the change, why it's needed, and any deadline."
            />
            <div className="lw-field">
              <label className="lw-field__label">Screenshots / images (optional)</label>
              {attachments.length > 0 && (
                <div className="lw-attachlist">
                  {attachments.map((url) => (
                    <span key={url} className="lw-attachlist__item">
                      <img src={url} alt="Attachment" />
                      <button
                        type="button"
                        className="lw-attachlist__remove"
                        title="Remove"
                        onClick={() => setAttachments((prev) => prev.filter((u) => u !== url))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  uploadFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <div>
                <button
                  type="button"
                  className="lw-btn lw-btn--secondary lw-btn--sm"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? "Uploading…" : "+ Add images"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="lw-modal__foot">
          <button type="button" className="lw-btn lw-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="lw-btn lw-btn--primary" disabled={submitting} onClick={submit}>
            {submitting ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TicketCard({ ticket, onUpdate }) {
  const { api, API_BASE } = useCms();
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const setStatus = async (status) => {
    setBusy(true);
    const res = await api.post(`${API_BASE}/tickets/update`, { ticketId: ticket._id, status });
    setBusy(false);
    if (res && res.success) {
      onUpdate(res.ticket);
    } else {
      toast.error(res?.error || "Update failed");
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    const res = await api.post(`${API_BASE}/tickets/comment`, {
      ticketId: ticket._id,
      text: comment.trim(),
    });
    setBusy(false);
    if (res && res.success) {
      setComment("");
      onUpdate(res.ticket);
    } else {
      toast.error(res?.error || "Comment failed");
    }
  };

  return (
    <article className={`lw-ticket lw-ticket--${ticket.status}`}>
      <button type="button" className="lw-ticket__head" onClick={() => setExpanded((v) => !v)}>
        <div className="lw-ticket__headmain">
          <span className="lw-ticket__number">#{ticket.ticketNumber}</span>
          <span className="lw-ticket__title">{ticket.title}</span>
          {ticket.page && <span className="lw-ticket__page">{ticket.page}</span>}
        </div>
        <div className="lw-ticket__headside">
          <span className={`lw-badge lw-badge--priority-${ticket.priority}`}>
            {PRIORITY_LABELS[ticket.priority] || ticket.priority}
          </span>
          <span className={`lw-badge lw-badge--status-${ticket.status}`}>
            {STATUS_LABELS[ticket.status] || ticket.status}
          </span>
          <span className="lw-row__meta">
            {ticket.createdBy?.fullName ? `${ticket.createdBy.fullName} · ` : ""}
            {timeAgo(ticket.createdTime)}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="lw-ticket__body">
          {ticket.description && <p className="lw-ticket__desc">{ticket.description}</p>}

          <TicketAttachments attachments={ticket.attachments} />

          <div className="lw-ticket__statusrow">
            <span className="lw-field__label">Status:</span>
            {STATUS_ORDER.map((status) => (
              <button
                key={status}
                type="button"
                disabled={busy || ticket.status === status}
                className={`lw-chip ${ticket.status === status ? "lw-chip--active" : ""}`}
                onClick={() => setStatus(status)}
              >
                {STATUS_LABELS[status]}
              </button>
            ))}
          </div>

          {(ticket.comments || []).length > 0 && (
            <ul className="lw-comments">
              {ticket.comments.map((c, i) => (
                <li key={i} className="lw-comments__item">
                  <p className="lw-comments__meta">
                    <strong>{c.authorName || c.authorEmail}</strong> · {formatTime(c.createdTime)}
                  </p>
                  <p className="lw-comments__text">{c.text}</p>
                </li>
              ))}
            </ul>
          )}

          <div className="lw-comments__composer">
            <input
              className="lw-input"
              type="text"
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addComment()}
            />
            <button type="button" className="lw-btn lw-btn--secondary lw-btn--sm" disabled={busy || !comment.trim()} onClick={addComment}>
              Comment
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export function TicketsPage() {
  const { api, API_BASE } = useCms();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get(`${API_BASE}/tickets`);
    if (res && res.success) setTickets(res.tickets || []);
    setLoading(false);
  }, [api, API_BASE]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = tickets.filter((t) => {
    if (filter === "active") return t.status === "open" || t.status === "in-progress";
    if (filter === "closed") return t.status === "done" || t.status === "declined";
    return true;
  });

  return (
    <div className="lw-page">
      <PageHeader
        title="Change requests"
        subtitle="Log website tweaks that need a developer — the site maintainer is notified by email."
        actions={
          <button type="button" className="lw-btn lw-btn--primary" onClick={() => setCreateOpen(true)}>
            + New request
          </button>
        }
      />

      <div className="lw-filterrow">
        {[
          { value: "active", label: "Active" },
          { value: "closed", label: "Closed" },
          { value: "all", label: "All" },
        ].map((f) => (
          <button
            key={f.value}
            type="button"
            className={`lw-chip ${filter === f.value ? "lw-chip--active" : ""}`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="lw-empty">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="lw-empty">
          {filter === "active" ? "No active requests — nice and quiet." : "Nothing here."}
        </p>
      ) : (
        <div className="lw-ticketlist">
          {filtered.map((ticket) => (
            <TicketCard
              key={ticket._id}
              ticket={ticket}
              onUpdate={(updated) =>
                setTickets((prev) => prev.map((t) => (t._id === updated._id ? updated : t)))
              }
            />
          ))}
        </div>
      )}

      {createOpen && (
        <NewTicketModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}
